import { LangstonContentResponse } from "@/types/models";
import {
  ADMIN_CONSOLE_ADMIN_TOOLS_CONTENT_SETTINGS,
  BROWSE,
  SEARCH,
  VIEW_ARTICLE_VEILED_BOTTOM,
  VIEW_ARTICLE_VEILED_BOTTOM_POST_TRIAL,
  VIEW_ARTICLE_VEILED_BOTTOM_UNAUTHENTICATED,
  VIEW_ARTICLE_VEILED_TOP,
  VIEW_VIDEO_VEILED_BOTTOM,
  VIEW_VIDEO_VEILED_BOTTOM_POST_TRIAL,
  VIEW_VIDEO_VEILED_TOP,
} from "./constants";
import propConfigMap from "./propConfigMap";
import UnlockOrPreviewContentBanner from "./UnlockOrPreviewContentBanner";
import useMeta from "./useMeta";

export { ADMIN_CONSOLE_ADMIN_TOOLS_CONTENT_SETTINGS } from "./constants";

export { HideWhenUnlockOrPreviewContentBannerIsShown as default } from "./UnlockOrPreviewContentBanner";

interface UnlockOrPreviewContentBannerForViewVeiledTopComponentProps {
  isVideo: boolean;
  isPremiumContent: boolean;
}

interface UnlockOrPreviewContentBannerForViewVeiledBottomComponentProps {
  isVideo: boolean;
  content?: LangstonContentResponse | null | undefined;
}

export const UnlockOrPreviewContentBannerForSearch = () => (
  <UnlockOrPreviewContentBanner location={SEARCH} />
);

export const UnlockOrPreviewContentBannerForBrowse = () => (
  <UnlockOrPreviewContentBanner location={BROWSE} />
);

export const UnlockOrPreviewContentBannerForViewVeiledTop = (() => {
  const UnlockOrPreviewContentBannerForViewVeiledTopComponent = ({
    isVideo,
    isPremiumContent,
  }: UnlockOrPreviewContentBannerForViewVeiledTopComponentProps) => {
    const { isLiteUser, isParent, canStartPremiumTrial } = useMeta();

    // Don't show top banner if parent / caregiver
    if (isLiteUser && isParent) return null;

    // Do not render a top view page top banner for a
    // user that is unable to start a premium trial.
    if (!canStartPremiumTrial) return null;

    // Lite unlock banners should only render
    // on pages that are premium content.
    if (!isPremiumContent) return null;

    return (
      <UnlockOrPreviewContentBanner
        location={isVideo ? VIEW_VIDEO_VEILED_TOP : VIEW_ARTICLE_VEILED_TOP}
      />
    );
  };

  return UnlockOrPreviewContentBannerForViewVeiledTopComponent;
})();

export const UnlockOrPreviewContentBannerForViewVeiledBottom = (() => {
  const UnlockOrPreviewContentBannerForViewVeiledBottomComponent = ({
    isVideo,
    content,
  }: UnlockOrPreviewContentBannerForViewVeiledBottomComponentProps) => {
    const { isAuthenticatedUser, isParent, canStartPremiumTrial } = useMeta();

    if (!isAuthenticatedUser) {
      return (
        <UnlockOrPreviewContentBanner
          location={VIEW_ARTICLE_VEILED_BOTTOM_UNAUTHENTICATED}
        />
      );
    }

    // Lite unlock banners should only render
    // on pages that are premium content
    if (!content?.data?.is_premium) return null;

    // Default page location configuration is based on isVideo
    let location = isVideo
      ? VIEW_VIDEO_VEILED_BOTTOM
      : VIEW_ARTICLE_VEILED_BOTTOM;

    // Render a different configured bottom view page banner for a
    // non parent user that is unable to start a premium trial.
    if (!isParent && !canStartPremiumTrial) {
      // Add 'PostTrial' to location for a
      // lookup check in the configuration map.
      const postTrialPermutationKey = isVideo
        ? VIEW_VIDEO_VEILED_BOTTOM_POST_TRIAL
        : VIEW_ARTICLE_VEILED_BOTTOM_POST_TRIAL;

      // If no post trial permutation is registered in the
      // config we will not show a banner for a post trial.
      if (propConfigMap.has(postTrialPermutationKey)) {
        // Since there is a config registered adjust location so
        // that we will use that permutation for displaying.
        location = postTrialPermutationKey;
      }
    }

    return <UnlockOrPreviewContentBanner location={location} />;
  };

  return UnlockOrPreviewContentBannerForViewVeiledBottomComponent;
})();

export const UnlockOrPreviewContentBannerForAdminToolsContentSettings = () => (
  <UnlockOrPreviewContentBanner
    location={ADMIN_CONSOLE_ADMIN_TOOLS_CONTENT_SETTINGS}
  />
);
